<template>
    <section class="relative" :class="{
        'forceright': blogStore.articleHasTag(article, 'ImagesToTheRight'),
        'forceleft': blogStore.articleHasTag(article, 'ImagesToTheLeft'),
        'bg-gravaasuperlightwarmgray': blogStore.articleHasTag(article, 'Bgwarmgray')
    }">
        <!-- Image Section ImageFlip.vue -->
        <figure class="w-full flex">
            <UiArticleImage 
                :fit="'crop'" 
                :modifiers="modifiers" 
                v-if="article.image" 
                :article="article" 
                class="w-1/2 object-cover"
                :showcaption="showcaption"
                :overlay="false"
                :sizes="'sm:1024px md:1024px lg:1280px xl:1500'"
                :class="{'w-2/5 mr-28 ml-28': blogStore.articleHasTag(article, 'Small'), 'w-1/2': !blogStore.articleHasTag(article, 'Small')}"
                :ar="blogStore.articleHasTag(article, 'Small') ? '561:500' : '705:705'"                 
            >
            <div v-if="showcaption && article.altText" class="text-white bg-gravaablack/70 p-5 m-0 ">
                {{article.altText}}
            </div>
            </UiArticleImage>
        </figure>

        <!-- Text alignment handled via CSS -->
        <UiContentwrapper class="md:absolute inset-0 justify-center items-center">
            <!-- Left-aligned text -->
            <UiNarrowColumnLeft class="nth-left mr-8">
                <div  v-html="cleanHtml(article.contenthtml)"/>
                <div v-if="article.excerpt" class="excerpt-button">
                    <NuxtLink :to="extractLink(article.excerpthtml)">
                        <UiRippleButton class="black">
                        {{ article.excerpt }}
                        </UiRippleButton>
                    </NuxtLink>
                </div>                                    
            </UiNarrowColumnLeft>

            <!-- Right-aligned text -->
            <UiNarrowColumnRight 
                class="nth-right" 
                :class="{'ml-0': blogStore.articleHasTag(article, 'Small'), 'ml-8': !blogStore.articleHasTag(article, 'Small')}">
                <div v-html="cleanHtml(article.contenthtml)"/>
                <div v-if="article.excerpt" class="excerpt-button">
                    <NuxtLink :to="extractLink(article.excerpthtml)">
                        <UiRippleButton class="black">
                        {{ article.excerpt }}
                        </UiRippleButton>
                    </NuxtLink>
                </div>                                  
            </UiNarrowColumnRight>
        </UiContentwrapper>
    </section>
</template>

<script setup>
import { useBlogStore } from '@/stores/blogStore';
import { computed } from 'vue';

const blogStore = useBlogStore();

// Props
const props = defineProps({
    article: {
        type: Object,
        required: true
    },
    showcaption: {
        type: Boolean,
        default: false
    }
});

// Computed property for modifiers based on tags
const modifiers = computed(() => {
    if (blogStore.articleHasTag(props.article, 'ExtraTall')) {
        return { auto: 'format,compress', ar: '705:1132' };
    } else if (blogStore.articleHasTag(props.article, 'Tall')) {
        return { auto: 'format,compress', ar: '705:1000' };
    } else if (blogStore.articleHasTag(props.article, 'Medium')) {
        return { auto: 'format,compress', ar: '720:600' };
    } else if (blogStore.articleHasTag(props.article, 'Small')) {
        return { auto: 'format,compress', ar: '561:500' };
    } else {
        return { auto: 'format,compress', ar: '705:705' };
    }
});

// Function to clean HTML content, keeping only allowed tags
const cleanHtml = (html) => {
  // Define allowed tags
  const allowedTags = ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'strong', 'em', 'a', 'br'];

  // Remove meta tags explicitly
  html = html.replace(/<meta[^>]*>/gi, '');

  // Use regex to remove unwanted tags while keeping allowed ones
  const regex = /<\/?([a-z][a-z0-9]*)[^>]*>/gi;

  return html.replace(regex, (match, tag) => {
    return allowedTags.includes(tag.toLowerCase()) ? match : '';
  });
};


const extractLink = (excerptHtml) => {
    const linkRegex = /href=["'](.*?)["']/;
    const match = excerptHtml.match(linkRegex);
    return match ? match[1] : '#';
  };
</script>

<style scoped>

  :deep(ul){
    padding-left: 16px;
  }

  :deep(li){
    list-style-type: disc;
  }

/* Move image to the right for odd sections */
section:nth-child(odd) figure {
    justify-content: flex-end;
}

/* Show left-aligned text for odd sections */
section:nth-child(odd) .nth-left {
    display: block;
}
section:nth-child(odd) .nth-right {
    display: none;
}

/* Show right-aligned text for even sections */
section:nth-child(even) .nth-left {
    display: none;
}
section:nth-child(even) .nth-right {
    display: block;
}

/* Adjust forced right layout */
section.forceright figure {
    justify-content: flex-end !important;
    margin-left: 14px !important;
}

section.forceright .nth-left {
    display: block !important;
}
section.forceright .nth-right {
    display: none !important;
}

/* Adjust forced left layout */
section.forceleft figure {
    justify-content: flex-start !important;
    margin-right: 14px !important;
}

section.forceleft .nth-left {
    display: none !important;
}
section.forceleft .nth-right {
    display: block !important;
}

/* Adjust layout for medium and smaller screens */
@media (max-width: 1100px) {
    section {
        display: flex;
        flex-direction: column-reverse; /* Reverse the order: text first, then image */
    }

    section figure {
        width: 100%;
        justify-content: center;
        margin-left: 0;
        margin-right: 0;
    }

    /* Target the image directly instead of using the class name */
    section figure > * {
        width: 94%; /* Make the image span the full width */
        height: auto; /* Keep the aspect ratio */
    }

    section .nth-left,
    section .nth-right {
        display: block;
        
        text-align: left; /* Center-align the text */
    }

    section .nth-left {
        margin-right: 0;
    }

    section .nth-right {
        margin-left: 0;
    }

   

    section .flex {
        flex-direction: column;
        align-items: center;
    }

        /* Center forced left/right images */
        section.forceleft figure,
    section.forceright figure {
        justify-content: center !important;
        margin-left: 0 !important;
        margin-right: 0 !important;
    }

    .excerpt-button {
    margin-bottom: 24px; /* Spacing on exerpt button on small screens */
  }
}


</style>